import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProviderNpiValue } from "@/components/Selects/ProviderNpiSelect";
import { useTRPC } from "@/trpc";
import { NPI_PATTERN } from "@/features/credentials/components/license-verification/types";
import type { StartVerificationDialogProps } from "@/features/credentials/components/primary-source-runs/types";
import { invalidateCredentialVaultRecord } from "@/features/credentials/lib/credential-vault-invalidation";
import {
  changedLicenses,
  createLicenseDrafts,
  hasNewDuplicateStates,
  isLicenseIncluded,
  issuesForIncludedLicenses,
  mergeLicenseSuggestions,
  savedFactsMatch,
  type LicenseDraft,
  type LicenseSuggestion,
} from "./license-drafts";
import {
  MAX_PREPARATION_LICENSES,
  type VerificationPreparation,
} from "./types";

export function usePreparationEditor({
  initialPreparation,
  scope,
  onStarted,
  onReload,
}: Pick<StartVerificationDialogProps, "scope" | "onStarted"> & {
  initialPreparation: VerificationPreparation;
  onReload: () => Promise<VerificationPreparation>;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [preparation, setPreparation] = useState(initialPreparation);
  const [licenses, setLicenses] = useState(() =>
    createLicenseDrafts(initialPreparation),
  );
  // Per-run selection by state; saved license states are fixed, so a state
  // maps to the same rows for the life of the dialog.
  const [skippedStateCodes, setSkippedStateCodes] = useState<string[]>([]);
  const [selectedNpi, setSelectedNpi] = useState<ProviderNpiValue | null>(null);
  const [suggestions, setSuggestions] = useState<LicenseSuggestion[]>([]);
  const [lookupWarning, setLookupWarning] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [action, setAction] = useState<"lookup" | "start" | "reload" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const confirmNpi = useMutation(
    trpc.psv.confirmVerificationNpi.mutationOptions(),
  );
  const save = useMutation(
    trpc.psv.saveVerificationPreparation.mutationOptions(),
  );
  const start = useMutation(trpc.psv.startVerificationRun.mutationOptions());
  const pending = action !== null;
  const npiReady = NPI_PATTERN.test(preparation.npi ?? "");
  const changes = changedLicenses({ preparation, licenses });
  const duplicates = hasNewDuplicateStates(licenses);
  const includedLicenses = licenses.filter((license) =>
    isLicenseIncluded({ license, skippedStateCodes }),
  );
  const skippedCount = licenses.length - includedLicenses.length;
  const noneSelected = licenses.length > 0 && includedLicenses.length === 0;
  const unchangedIssue = issuesForIncludedLicenses({
    issues: preparation.licenseIssues,
    licenses,
    skippedStateCodes,
  }).some(
    (issue) =>
      !changes.some((license) => license.credentialId === issue.credentialId),
  );
  const tooManyChanges = changes.length > MAX_PREPARATION_LICENSES;
  const ready =
    npiReady &&
    Boolean(preparation.providerName) &&
    !noneSelected &&
    includedLicenses.length > 0 &&
    includedLicenses.every((license) =>
      Boolean(license.number.trim() && license.stateCode),
    ) &&
    !duplicates &&
    !unchangedIssue &&
    !tooManyChanges;

  function remember(saved: VerificationPreparation) {
    setPreparation(saved);
    queryClient.setQueryData(
      trpc.psv.getVerificationPreparation.queryKey(scope),
      saved,
    );
    void invalidateCredentialVaultRecord({
      queryClient,
      trpc,
      scope: { kind: "organization", ...scope },
    });
  }

  function selectNpi(value: ProviderNpiValue | null) {
    setSelectedNpi(value);
    setConfirmed(false);
    setError(null);
  }

  function editLicenses(next: LicenseDraft[]) {
    setLicenses(next);
    setConfirmed(false);
    setError(null);
  }

  function setStateIncluded({
    stateCode,
    included,
  }: {
    stateCode: string;
    included: boolean;
  }) {
    setSkippedStateCodes((current) => {
      const others = current.filter((code) => code !== stateCode);
      return included ? others : [...others, stateCode];
    });
    setConfirmed(false);
    setError(null);
  }

  async function fillLicenses() {
    const number = npiReady ? preparation.npi : selectedNpi?.npi;
    if (!number || pending) {
      return;
    }
    setAction("lookup");
    setError(null);
    setConfirmed(false);
    try {
      const result = await confirmNpi.mutateAsync({
        ...scope,
        expectedVersion: preparation.expectedVersion,
        npi: {
          number,
          taxonomyCodes: selectedNpi?.specialties.map(
            (specialty) => specialty.code,
          ),
        },
      });
      remember(result.preparation);
      setSuggestions(result.licenseSuggestions);
      setLookupWarning(result.lookupWarning);
      setLicenses((current) =>
        mergeLicenseSuggestions({
          licenses: current,
          suggestions: result.licenseSuggestions,
        }),
      );
      setSelectedNpi(null);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Couldn’t confirm the NPI. Try again.",
      );
    } finally {
      setAction(null);
    }
  }

  async function startVerification() {
    if (!ready || !confirmed || pending) {
      return;
    }
    setAction("start");
    setError(null);
    try {
      let saved = preparation;
      if (changes.length) {
        saved = await save.mutateAsync({
          ...scope,
          expectedVersion: preparation.expectedVersion,
          licenses: changes.map(({ credentialId, number, stateCode }) => ({
            credentialId,
            number: number.trim(),
            stateCode,
          })),
        });
        remember(saved);
        setLicenses(createLicenseDrafts(saved));
      }
      // The run is limited to the states named here, so a license saved
      // after this dialog opened is never verified without being chosen.
      const stateCodes = [
        ...new Set(
          [...saved.licenses, ...saved.sources].flatMap((entry) =>
            entry.stateCode ? [entry.stateCode] : [],
          ),
        ),
      ].filter((code) => !skippedStateCodes.includes(code));
      const includedSources = saved.sources.filter((source) =>
        stateCodes.includes(source.stateCode ?? ""),
      );
      if (
        issuesForIncludedLicenses({
          issues: saved.licenseIssues,
          licenses: createLicenseDrafts(saved),
          skippedStateCodes,
        }).length ||
        !savedFactsMatch({
          preparation: saved,
          npi: preparation.npi,
          providerName: preparation.providerName,
          licenses,
        })
      ) {
        setConfirmed(false);
        setError(
          "Review the saved license details below and confirm them again before starting.",
        );
        return;
      }
      const result = await start.mutateAsync({
        ...scope,
        sourceIds: includedSources.map((source) => source.sourceId),
        licenseIds: includedSources.flatMap((source) =>
          source.licenseNumbers.map((number) => ({
            sourceId: source.sourceId,
            number,
          })),
        ),
        stateCodes,
        allowNameSearch: false,
        confirmation: {
          expectedVersion: saved.expectedVersion,
          confirmed: true,
        },
      });
      await onStarted(result);
    } catch (failure) {
      setConfirmed(false);
      setError(
        failure instanceof Error
          ? failure.message
          : "Couldn’t start verification. Review the details and try again.",
      );
    } finally {
      setAction(null);
      void queryClient.invalidateQueries({
        queryKey: trpc.psv.getRunAllowance.queryKey({
          organizationBusinessUnitId: scope.organizationBusinessUnitId,
        }),
      });
    }
  }

  async function reload() {
    if (pending) {
      return;
    }
    setAction("reload");
    setConfirmed(false);
    try {
      const saved = await onReload();
      setPreparation(saved);
      setLicenses(createLicenseDrafts(saved));
      setSelectedNpi(null);
      setSuggestions([]);
      setLookupWarning(null);
      setError(null);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Couldn’t reload saved details.",
      );
    } finally {
      setAction(null);
    }
  }

  return {
    preparation,
    licenses,
    skippedStateCodes,
    skippedCount,
    noneSelected,
    setStateIncluded,
    selectedNpi,
    suggestions,
    lookupWarning,
    confirmed,
    setConfirmed,
    action,
    pending,
    error,
    ready,
    npiReady,
    duplicates,
    tooManyChanges,
    dirty: changes.length > 0,
    selectNpi,
    editLicenses,
    fillLicenses,
    startVerification,
    reload,
  };
}
